import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Calendar, Truck, Package, ArrowRight, Eye, FileText, Printer, AlertCircle } from "lucide-react";
import SLABadge from "../components/SLABadge";
import CollapsiblePdfPreview from "../components/CollapsiblePdfPreview";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

function hoursSince(iso) {
  if (!iso) return 0;
  try { return (Date.now() - new Date(iso).getTime()) / 3600000; } catch { return 0; }
}

const STATUS_COLORS = {
  AGENT: "bg-blue-50 text-blue-700 border-blue-200",
  SCHEDULED: "bg-purple-50 text-purple-700 border-purple-200",
  IN_TRANSIT: "bg-amber-50 text-amber-700 border-amber-200",
  JHB_DEPOT: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

export default function ThirdPartyDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDates, setScheduleDates] = useState({});
  const [refNumbers, setRefNumbers] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => { loadJobs(); }, []);

  // Real-time polling - refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(loadJobs, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadJobs = async () => {
    try {
      const { data } = await api.get("/job-cards");
      // Assigned Jobs screen shows only AGENT status; once scheduled, it lives on /jobs (All Jobs Kanban)
      const assigned = (data.items || []).filter((j) => j.status === "AGENT");
      setJobs(assigned);
    } catch { toast.error("Failed to load jobs"); }
    setLoading(false);
  };

  const handleAction = async (jobId, action, body = null) => {
    setActionLoading((p) => ({ ...p, [jobId]: action }));
    try {
      if (body) {
        await api.put(`/job-cards/${jobId}/${action}`, body);
      } else {
        await api.put(`/job-cards/${jobId}/${action}`);
      }
      toast.success(`Job updated: ${action.replace("-", " ")}`);
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setActionLoading((p) => ({ ...p, [jobId]: null }));
  };

  if (loading) return <div className="text-slate-500 p-8">Loading jobs...</div>;

  return (
    <div data-testid="3pa-dashboard" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <Truck size={24} className="text-teal-600" /> Assigned Jobs
        </h1>
        <p className="text-slate-500 text-sm mt-1">New collections / deliveries awaiting scheduling. Once scheduled, cards move to <b>All Jobs</b>.</p>
      </div>

      {jobs.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Truck size={40} className="mx-auto text-slate-400 mb-3" />
          <p className="text-slate-500">No assigned jobs</p>
        </div>
      )}

      <div className="space-y-3">
        {jobs.map((job) => (
          <div
            key={job.id}
            data-testid={`agent-job-${job.id}`}
            className="bg-white rounded-xl shadow-sm p-4 card-interactive"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading font-bold text-base text-slate-800">
                    {job.job_card_number}
                  </h3>
                  <Badge variant="outline" className={`status-badge ${STATUS_COLORS[job.status] || ""}`}>
                    {job.status}
                  </Badge>
                  <SLABadge sla={job.sla} />
                </div>
                {job.status === "AGENT" && !job.tpa_reference_number && hoursSince(job.date_allocated || job.date_created) > 24 && (
                  <div
                    data-testid={`agent-warn-${job.id}`}
                    className="flex items-center gap-2 px-3 py-1.5 mb-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
                  >
                    <AlertCircle size={13} className="flex-shrink-0" />
                    <span>Missing 3PA Reference &mdash; capture a ref and collection date below to schedule this JC.</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4 text-xs text-slate-500">
                  <div>
                    <span className="text-slate-400">Model:</span>{" "}
                    <span className="text-slate-700">{job.model || "-"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Store:</span>{" "}
                    <span className="text-slate-700">{job.store || "-"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Area:</span>{" "}
                    <span className="text-slate-700">{job.area || "-"}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {job.pdf_url && (
                  <>
                    <a
                      data-testid={`view-original-jc-${job.id}`}
                      href={`${API_URL}/api/files/${job.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs font-medium rounded-lg transition-colors"
                      title="View / Print original Skyworth Job Card PDF"
                    >
                      <Printer size={13} /> Print JC
                    </a>
                    <a
                      data-testid={`download-original-jc-${job.id}`}
                      href={`${API_URL}/api/files/${job.pdf_url}`}
                      download={job.original_filename || `${job.job_card_number || 'JobCard'}.pdf`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 text-xs font-medium rounded-lg transition-colors"
                      title="Download original Skyworth Job Card PDF"
                    >
                      <FileText size={13} /> PDF
                    </a>
                  </>
                )}
                <Button
                  data-testid={`view-detail-${job.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  className="text-slate-500 hover:text-teal-600"
                >
                  <Eye size={14} className="mr-1" /> View
                </Button>
              </div>
            </div>

            {/* Actions based on status */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-3">
              {job.status === "AGENT" && (
                <>
                  <Input
                    data-testid={`ref-number-${job.id}`}
                    type="text"
                    placeholder="3PA Reference #"
                    value={refNumbers[job.id] || ""}
                    onChange={(e) => setRefNumbers((p) => ({ ...p, [job.id]: e.target.value }))}
                    className="bg-white border-slate-300 text-sm w-44"
                  />
                  <Input
                    data-testid={`schedule-date-${job.id}`}
                    type="date"
                    value={scheduleDates[job.id] || ""}
                    onChange={(e) => setScheduleDates((p) => ({ ...p, [job.id]: e.target.value }))}
                    className="bg-white border-slate-300 text-sm w-44"
                  />
                  <Button
                    data-testid={`schedule-btn-${job.id}`}
                    onClick={() => {
                      if (!refNumbers[job.id]?.trim()) {
                        toast.error("Please enter your 3PA Reference Number before scheduling");
                        return;
                      }
                      if (!scheduleDates[job.id]) {
                        toast.error("Please select an estimated collection date");
                        return;
                      }
                      handleAction(job.id, "schedule", {
                        estimated_collection_date: scheduleDates[job.id],
                        reference_number: refNumbers[job.id].trim()
                      });
                    }}
                    disabled={!!actionLoading[job.id]}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <Calendar size={14} className="mr-1" /> Schedule
                  </Button>
                </>
              )}
              {job.status === "SCHEDULED" && (
                <Button
                  data-testid={`in-transit-btn-${job.id}`}
                  onClick={() => handleAction(job.id, "in-transit")}
                  disabled={!!actionLoading[job.id]}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Truck size={14} className="mr-1" /> Mark In Transit
                </Button>
              )}
              {job.status === "IN_TRANSIT" && (
                <Button
                  data-testid={`jhb-depot-btn-${job.id}`}
                  onClick={() => handleAction(job.id, "jhb-depot")}
                  disabled={!!actionLoading[job.id]}
                  size="sm"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Package size={14} className="mr-1" /> Arrived at JHB Depot
                </Button>
              )}
              {job.estimated_collection_date && (
                <span className="text-xs text-slate-400 ml-auto">
                  Est. Collection: {formatDate(job.estimated_collection_date)}
                  {job.tpa_reference_number && <> | Ref: <b>{job.tpa_reference_number}</b></>}
                </span>
              )}
            </div>

            {job.pdf_url && (
              <div className="mt-3">
                <CollapsiblePdfPreview
                  pdfUrl={job.pdf_url}
                  title="Original Skyworth Job Card"
                  downloadFilename={job.original_filename || `${job.job_card_number || "JobCard"}.pdf`}
                  testIdPrefix={`assigned-jc-${job.id}`}
                  height={450}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
